import {
  DEFAULT_CREATE_INVOICE_MODE,
  DEFAULT_ENABLED_CREATE_INVOICE_MODES,
  normalizeCreateInvoiceModeConfig,
} from "./createInvoiceModes";

export const DEFAULT_MINIMAL_BILL_ENTRY_CONFIG = {
  enabled: false,
  internalDecoratorIds: [],
  internalSupplierIds: [],
  hideVendorInvoiceFields: true,
};

export const DEFAULT_BILLING_CONFIGURATION = {
  minimalBillEntry: DEFAULT_MINIMAL_BILL_ENTRY_CONFIG,
  allowDetailedItemTaxCustomization: true,
};

export const DEFAULT_DEPOSIT_CHECK_CONFIGURATION = {
  financeRecipientUserIds: [],
};

export const DEFAULT_SALES_ORDER_SNAPSHOT_CONFIGURATION = {
  updateMode: "manual",
};

export const DEFAULT_STRIPE_PAYMENT_DESCRIPTIONS = {
  invoice: "Invoice for Order {orderId}",
  deposit: "Deposit for Order {orderId}",
  balance: "Balance for Order {orderId}",
  fullUpfront: "Full upfront payment for Order {orderId}",
  partial: "Partial invoice for Order {orderId}",
  progress: "Progress invoice for Order {orderId}",
};

export const DEFAULT_STRIPE_PAYMENTS_CONFIGURATION = {
  native: {
    descriptions: DEFAULT_STRIPE_PAYMENT_DESCRIPTIONS,
  },
  promoStores: {
    descriptions: {
      payment: "PromoStore {promoStoreName} - Order #{orderNumber}",
    },
  },
};

export const DEFAULT_QBO_IDENTIFIER_TEMPLATES = {
  invoiceCustomerMemo: "Order {orderId}",
  invoicePrivateNote: "Elite Core - Order {orderId} - Invoice {invoiceId}",
  billPrivateNote: "Elite Core - PO {poNumber} - Order {orderId}",
};

export const QBO_IDENTIFIER_TEMPLATE_FIELDS = [
  {
    key: "invoiceCustomerMemo",
    apiKey: "invoice_customer_memo_template",
    section: "Customer Invoice",
    label: "Customer Memo",
    description: "May appear on the customer-facing QuickBooks invoice.",
    variables: ["orderId", "invoiceId", "customerName", "amount", "currency"],
  },
  {
    key: "invoicePrivateNote",
    apiKey: "invoice_private_note_template",
    section: "Customer Invoice",
    label: "Private Note",
    description: "Internal QuickBooks accounting reference. Not intended for the customer.",
    variables: ["orderId", "invoiceId", "customerName", "amount", "currency"],
  },
  {
    key: "billPrivateNote",
    apiKey: "bill_private_note_template",
    section: "Vendor Bill",
    label: "Private Note",
    description: "Internal QuickBooks accounting reference for vendor bill review.",
    variables: ["orderId", "billId", "poNumber", "supplierName", "amount", "currency"],
  },
];

export const INVOICE_EMAIL_SENDER_MODES = Object.freeze({
  USER_SELECTABLE: "user_selectable",
  PREFERRED_USER: "preferred_user",
  CUSTOM_EMAIL: "custom_email",
});

export const DEFAULT_INVOICE_EMAIL_SENDER = Object.freeze({
  mode: INVOICE_EMAIL_SENDER_MODES.USER_SELECTABLE,
  preferredUserId: null,
  customEmail: "",
  customName: "",
});

export const isValidInvoiceSenderEmail = (value) =>
  /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(String(value || "").trim()) &&
  String(value || "").trim().length <= 254;

export const normalizeInvoiceEmailSender = (rawConfig = {}) => {
  const raw = rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig) ? rawConfig : {};
  const candidateId = Number(raw.preferredUserId);
  return {
    mode: Object.values(INVOICE_EMAIL_SENDER_MODES).includes(raw.mode)
      ? raw.mode : DEFAULT_INVOICE_EMAIL_SENDER.mode,
    preferredUserId: Number.isSafeInteger(candidateId) && candidateId > 0 ? candidateId : null,
    customEmail: String(raw.customEmail || "").trim().toLowerCase(),
    customName: String(raw.customName || "").trim().slice(0, 120),
  };
};

export const DEFAULT_FINANCIAL_CONFIGURATION = {
  createInvoice: {
    enabledModes: DEFAULT_ENABLED_CREATE_INVOICE_MODES,
    defaultMode: DEFAULT_CREATE_INVOICE_MODE,
    enableInvoiceCreationReview: false,
  },
  invoiceEmailSender: DEFAULT_INVOICE_EMAIL_SENDER,
  billing: DEFAULT_BILLING_CONFIGURATION,
  depositCheck: DEFAULT_DEPOSIT_CHECK_CONFIGURATION,
  salesOrderSnapshot: DEFAULT_SALES_ORDER_SNAPSHOT_CONFIGURATION,
  stripePayments: DEFAULT_STRIPE_PAYMENTS_CONFIGURATION,
};

export const normalizeMinimalBillEntryConfig = (rawConfig = {}) => {
  const internalDecoratorIds = Array.isArray(rawConfig?.internalDecoratorIds)
    ? Array.from(
        new Set(
          rawConfig.internalDecoratorIds
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0)
        )
      )
    : [];
  const internalSupplierIds = Array.isArray(rawConfig?.internalSupplierIds)
    ? Array.from(
        new Set(
          rawConfig.internalSupplierIds
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0)
        )
      )
    : [];

  return {
    enabled: rawConfig?.enabled === true,
    internalDecoratorIds,
    internalSupplierIds,
    hideVendorInvoiceFields: rawConfig?.hideVendorInvoiceFields !== false,
  };
};

export const normalizeBillingFinancialConfig = (rawConfig = {}) => ({
  ...rawConfig,
  minimalBillEntry: normalizeMinimalBillEntryConfig(rawConfig?.minimalBillEntry || {}),
  allowDetailedItemTaxCustomization: rawConfig?.allowDetailedItemTaxCustomization !== false,
});

export const normalizeDepositCheckFinancialConfig = (rawConfig = {}) => ({
  financeRecipientUserIds: Array.isArray(rawConfig?.financeRecipientUserIds)
    ? Array.from(
        new Set(
          rawConfig.financeRecipientUserIds
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0)
        )
      )
    : [],
});

export const normalizeSalesOrderSnapshotFinancialConfig = (rawConfig = {}) => ({
  updateMode: rawConfig?.updateMode === "automatic" ? "automatic" : "manual",
});

export const normalizeStripePaymentsFinancialConfig = (rawConfig = {}) => {
  const descriptions = rawConfig?.native?.descriptions || {};
  const promoStorePayment = String(rawConfig?.promoStores?.descriptions?.payment || "").trim();
  return {
    native: {
      descriptions: Object.fromEntries(
        Object.entries(DEFAULT_STRIPE_PAYMENT_DESCRIPTIONS).map(([type, fallback]) => {
          const value = typeof descriptions?.[type] === "string" ? descriptions[type].trim() : "";
          return [type, value || fallback];
        })
      ),
    },
    promoStores: {
      descriptions: {
        payment:
          promoStorePayment || DEFAULT_STRIPE_PAYMENTS_CONFIGURATION.promoStores.descriptions.payment,
      },
    },
  };
};

export const resolveStripePaymentDescriptionPreview = (template, context = {}) => (
  String(template || "").replace(/\{(orderId|invoiceId|customerName|amount|currency|paymentType)\}/g, (_, token) => {
    const value = context?.[token];
    return value === null || value === undefined || typeof value === "object" ? "" : String(value);
  })
);

export const resolveQboIdentifierPreview = (template, context = {}, variables = []) => {
  const supported = new Set(variables);
  return String(template || "").replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (match, token) => {
    if (!supported.has(token)) return match;
    const value = context?.[token];
    return value === null || value === undefined || typeof value === "object" ? "" : String(value);
  });
};

export const findUnsupportedQboIdentifierTokens = (template, variables = []) => {
  const supported = new Set(variables);
  const found = new Set();
  String(template || "").replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (_, token) => {
    if (!supported.has(token)) found.add(token);
    return "";
  });
  return Array.from(found);
};

export const normalizeFinancialConfiguration = (rawConfig = {}) => ({
  createInvoice: normalizeCreateInvoiceModeConfig(rawConfig?.createInvoice || {}),
  invoiceEmailSender: normalizeInvoiceEmailSender(rawConfig?.invoiceEmailSender),
  billing: normalizeBillingFinancialConfig(rawConfig?.billing || {}),
  depositCheck: normalizeDepositCheckFinancialConfig(rawConfig?.depositCheck || {}),
  salesOrderSnapshot: normalizeSalesOrderSnapshotFinancialConfig(
    rawConfig?.salesOrderSnapshot || {}
  ),
  stripePayments: normalizeStripePaymentsFinancialConfig(rawConfig?.stripePayments || {}),
});
